import { useCallback, useEffect, useState, CSSProperties } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Select,
  Card,
  Space,
  Alert,
  Divider,
  Tooltip,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../configureStore';
import { addBasicInfo, PII } from '../../redux/reducer/applicant';
import { states } from '../../utils';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  UserOutlined,
  HomeOutlined,
  LockOutlined,
  InfoCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { ConfigStepId } from '../../utils/configuration';

const { Title, Text } = Typography;


const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '24px',
  },
  card: {
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
  header: {
    marginBottom: '24px',
    textAlign: 'center' as const,
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    marginBottom: '16px',
    fontSize: '18px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  formSection: {
    backgroundColor: '#fafafa',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  ssnAddon: {
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  ssnAddonHover: {
    color: '#1890ff',
  },
  submitButton: {
    marginTop: '32px',
  },
};

interface BasicInfoFormParams {
  stepId: ConfigStepId;
}

const BasicInfoForm = ({
  stepId,
}: BasicInfoFormParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const config = useSelector<RootState, PII<boolean> | undefined>(({ applicant }) => applicant.applicant?.missingInfo)
  const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['applicant/UPDATE_APPLICANT']);
  const [form] = Form.useForm<Partial<PII>>();
  const [ssn, setSsn] = useState('');
  const [maskedSsn, setMaskedSsn] = useState('');
  const [showSSN, setShowSSN] = useState<boolean>(false);

  const onFinish = useCallback((values: Partial<PII>) => {
    dispatch(addBasicInfo({
      input: {
        pii: {
          ...values,
          tin: ssn.length ? ssn : undefined,
        },
        step: stepId,
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken') ?? ''}`,
      },
      message: { success: 'Successfully updated! Thank you!' },
    }))
  }, [ssn]);

  useEffect(() => {
    form.setFieldValue('tin', showSSN ? ssn : maskedSsn)
  }, [showSSN]);

  const formatSSN = (value: string) => {
    const digits = value.replace(/[^0-9*]/g, "").slice(0, 9); // Keep only numbers & limit to 9 digits
    let formatted = "";

    if (digits.length > 0) formatted += digits.slice(0, 3);
    if (digits.length > 3) formatted += "-" + digits.slice(3, 5);
    if (digits.length > 5) formatted += "-" + digits.slice(5);

    return formatted;
  };

  const maskSSN = (value: string) => {
    const digits = value.replace(/[^0-9*-]/g, "");
    if (digits.length <= 6) {
      let str = "*".repeat(digits.length);
      // @ts-expect-error it does exist
      if (digits.length >= 3) str = str.replaceAt(3, '-');
      return str
    }
    let str = "*".repeat(6) + digits.slice(6);
    // @ts-expect-error it does exist
    if (digits.length >= 3) str = str.replaceAt(3, '-');

    return str;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): { mask: string; non: string; } => {
    if (showSSN) {
      const formattedSSN = formatSSN(e.target.value);
      const mask = maskSSN(formattedSSN)
      setMaskedSsn(mask);
      setSsn(formattedSSN);
      return { mask, non: formattedSSN };
    } else {
      if (e.target.value.length > ssn.length) {
        const formattedSSN = formatSSN(ssn + ((e.target.value.length === 4 || e.target.value.length === 7) ? '-' : '') + e.target.value[e.target.value.length - 1]);
        const mask = maskSSN(formattedSSN)
        setMaskedSsn(mask);
        setSsn(formattedSSN);
        return { mask, non: formattedSSN };
      } else {
        const formattedSSN = formatSSN(ssn.substring(0, e.target.value.length - 1));
        const mask = maskSSN(formattedSSN)
        setMaskedSsn(mask);
        setSsn(formattedSSN);
        return { mask, non: formattedSSN };
      }
    }
  };

  return config ? (
    <div style={styles.container}>
      <Card style={styles.card}>
        <div style={styles.header}>
          <Title level={3}>
            <UserOutlined /> Complete Your Information
          </Title>
          <Text type="secondary">
            Please provide the following information to continue with your application
          </Text>
        </div>

        <Alert
          message="Required Information"
          description="All fields marked with * are required. Your information is encrypted and secure."
          type="info"
          showIcon
          closable
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: '24px' }}
        />

        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          {/* Personal Information Section */}
          {(config.firstName || config.lastName) && (
            <div style={styles.formSection}>
              <Title level={5} style={styles.sectionTitle}>
                <UserOutlined /> Personal Information
              </Title>
              <Row gutter={16}>
                {config.firstName && (
                  <Col span={config.lastName ? 12 : 24}>
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[{ required: true, message: 'Please enter your first name' }]}
                      tooltip="Your legal first name as it appears on official documents"
                    >
                      <Input
                        placeholder="Enter first name"
                        prefix={<UserOutlined />}
                      />
                    </Form.Item>
                  </Col>
                )}
                {config.lastName && (
                  <Col span={config.firstName ? 12 : 24}>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[{ required: true, message: 'Please enter your last name' }]}
                      tooltip="Your legal last name as it appears on official documents"
                    >
                      <Input
                        placeholder="Enter last name"
                        prefix={<UserOutlined />}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </div>
          )}

          {/* Address Information Section */}
          {(config.addressLine1 || config.city || config.state || config.postalCode) && (
            <div style={styles.formSection}>
              <Title level={5} style={styles.sectionTitle}>
                <HomeOutlined /> Address Information
              </Title>

              {config.addressLine1 && (
                <>
                  <Form.Item
                    name="addressLine1"
                    label="Street Address"
                    rules={[{ required: true, message: 'Please enter your address' }]}
                  >
                    <Input
                      placeholder="Enter street address"
                      prefix={<HomeOutlined />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="addressLine2"
                    label="Address Line 2"
                  >
                    <Input
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </Form.Item>
                </>
              )}

              {(config.city || config.state || config.postalCode) && (
                <Row gutter={16}>
                  {config.city && (
                    <Col span={12}>
                      <Form.Item
                        name="city"
                        label="City"
                        rules={[{ required: true, message: 'Please enter your city' }]}
                      >
                        <Input placeholder="Enter city" />
                      </Form.Item>
                    </Col>
                  )}
                  {config.state && (
                    <Col span={6}>
                      <Form.Item
                        name="state"
                        label="State"
                        rules={[
                          { required: true, message: 'Please select your state' },
                        ]}
                      >
                        <Select
                          options={states.map((value) => ({ value, label: value }))}
                          placeholder="Select state"
                          showSearch
                          aria-label='Select your state'
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {config.postalCode && (
                    <Col span={6}>
                      <Form.Item
                        name="postalCode"
                        label="ZIP Code"
                        rules={[
                          { required: true, message: 'Please enter your ZIP code' },
                          { pattern: /^\d{5}$/, message: 'Please enter a valid 5-digit ZIP code' }
                        ]}
                      >
                        <Input placeholder="12345" maxLength={5} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              )}
            </div>
          )}

          {/* Sensitive Information Section */}
          {config.tin && (
            <div style={styles.formSection}>
              <Title level={5} style={styles.sectionTitle}>
                <LockOutlined /> Secure Information
              </Title>

              <Alert
                message="SSN Security"
                description="Your Social Security Number is encrypted and stored securely. We use bank-level encryption to protect your data."
                type="success"
                showIcon
                closable
                style={{ marginBottom: '16px' }}
              />

              <Form.Item
                name="tin"
                label={
                  <Space>
                    <span>Social Security Number</span>
                    <Tooltip title="Required for identity verification and tax purposes">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
                rules={[
                  { required: true, message: 'Please enter your Social Security Number' },
                  { pattern: /^[\d*]{3}-?[\d*]{2}-?\d{4}$/, message: 'Please enter a valid SSN (XXX-XX-XXXX)' }
                ]}
                getValueFromEvent={(event) => {
                  const { mask, non } = handleChange(event)
                  return showSSN ? non : mask;
                }}
              >
                <Input
                  onChange={handleChange}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  prefix={<LockOutlined />}
                  addonAfter={
                    <Tooltip title={showSSN ? "Hide SSN" : "Show SSN"}>
                      <div
                        style={styles.ssnAddon}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#1890ff'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                      >
                        {showSSN ?
                          <EyeInvisibleOutlined onClick={() => setShowSSN(!showSSN)} /> :
                          <EyeOutlined onClick={() => setShowSSN(!showSSN)} />
                        }
                      </div>
                    </Tooltip>
                  }
                />
              </Form.Item>
            </div>
          )}

          <Divider />

          <Form.Item style={styles.submitButton}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              icon={<SaveOutlined />}
            >
              Submit Information
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  ) : null;
};

export default BasicInfoForm;